import { Card, CardContent, Link, Typography } from "@mui/material";
import {
  getGridStringOperators,
  type GridColDef,
  type GridFilterModel,
  GridLogicOperator,
  type GridRenderCellParams,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext.tsx";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { projectsPrefix } from "../../../services/ProjectsServices.ts";
import { getDiffDaysMessage } from "../../../utils/main.ts";
import AccordionTable from "../../datagrid/AccordionTable.tsx";
import BaseTable, { type BaseTableProps } from "../../datagrid/BaseTable.tsx";
import EmptyCard from "../EmptyCard.jsx";
import VisitsDrawer from "../VisitsDrawer.tsx";

interface FlowsheetDataTableProps {
  encounterId: string | null;
  countTotal?: number;
  highlightTotal?: number;
  eagerLoad?: boolean;
}

export default function FlowsheetDataTable({
  encounterId,
  countTotal,
  highlightTotal,
  eagerLoad = false,
}: FlowsheetDataTableProps) {
  const { id: projectId, flowsheetGroups } = useContext(ProjectContext)!;
  const { id } = useContext(PatientContext)!;

  const [open, setOpen] = useState(false);
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(
    null,
  );

  function toggleOpen(encounterId: string | null) {
    setSelectedEncounterId(encounterId);
    setOpen((prev) => !prev);
  }

  const {
    error: flowsheetError,
    create: fetchFlowsheet,
    isLoading: flowsheetLoading,
  } = useFetch<PagedResponse<Flowsheet>, FilterPageSortRequest>();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "assessmentDatetime", sort: "asc" },
  ]);
  const [filter, setFilter] = useState<GridFilterModel>();
  const [rows, setRows] = useState<Flowsheet[]>([]);
  const [rowCount, setRowCount] = useState(countTotal || 0);
  const [shouldFetchFlowsheet, setShouldFetchFlowsheet] = useState(eagerLoad);
  const [currentEncounterId, setCurrentEncounterId] = useState(encounterId);
  const [viewStateReady, setViewStateReady] = useState(false);

  // Only keep 'contains' default operators
  const stringFilterOperators = getGridStringOperators().filter(
    (operator) => operator.value === "contains",
  );

  useEffect(() => {
    if (currentEncounterId !== encounterId) {
      setShouldFetchFlowsheet(true);
    }
  }, [encounterId, setShouldFetchFlowsheet, currentEncounterId]);

  const flowsheetTableFields: GridColDef<Flowsheet>[] = useMemo(
    () => [
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 100,
        filterOperators: stringFilterOperators,
      },
      {
        field: "result",
        headerName: "Result",
        flex: 1,
        valueGetter: (value, row) => {
          return row.unitOfMeasure ? `${value} ${row.unitOfMeasure}` : value;
        },
        minWidth: 100,
        filterOperators: stringFilterOperators,
      },
      {
        field: "assessmentDatetime",
        headerName: "Date",
        flex: 2,
        valueGetter: (_value, row) => {
          const daysOffset = getDiffDaysMessage(row.assessmentOffset);
          return `${daysOffset}, ${row.assessmentTime}`;
        },
        minWidth: 100,
        filterable: false,
      },
      ...(encounterId === null
        ? [
            {
              field: "encounterNumber",
              headerName: "Source",
              flex: 1,
              // Disable sorting & filtering because it's a derived field after data is fetched and cannot use out of the box sql sorting
              sortable: false,
              filterable: false,
              renderCell: (params: GridRenderCellParams<Flowsheet>) => {
                const row = params.row;
                if (row.encounterId === null) {
                  return "no source visit";
                }
                return (
                  <Link
                    onClick={() => toggleOpen(row.encounterId)}
                    component={"button"}
                    variant={"subtitle2"}
                  >
                    {`Visit ID ${row.encounterNumber}`}
                  </Link>
                );
              },
            },
          ]
        : []),
    ],
    [encounterId, stringFilterOperators],
  );

  const isCardEmpty = useMemo(() => countTotal === 0, [countTotal]);

  useEffect(() => {
    if (
      flowsheetGroups &&
      flowsheetGroups.length > 0 &&
      shouldFetchFlowsheet &&
      viewStateReady &&
      !flowsheetLoading &&
      !flowsheetError
    ) {
      let requestPath = null;
      switch (encounterId) {
        case null:
          requestPath = `${projectsPrefix}/${projectId}/patients/${id}/flowsheets/`;
          break;
        case "index":
          requestPath = `${projectsPrefix}/${projectId}/patients/${id}/encounters/index/flowsheet`;
          break;
        default:
          requestPath = `${projectsPrefix}/${projectId}/patients/${id}/encounters/${encounterId}/flowsheet`;
      }

      void fetchFlowsheet({
        path: `${requestPath}`,
        body: {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: [...sortModel],
          search: filter?.quickFilterValues?.join(" ") || "",
          filters: filter?.items,
          filterLogicalOperator: filter?.logicOperator?.toUpperCase(),
        },
        postResponseAction: (data) => {
          setRows(data?.results ?? []);
          setRowCount(data?.rowCount ?? 0);
          setShouldFetchFlowsheet(false);
          setCurrentEncounterId(encounterId);
        },
      });
    }
  }, [
    fetchFlowsheet,
    id,
    projectId,
    flowsheetLoading,
    flowsheetError,
    shouldFetchFlowsheet,
    viewStateReady,
    paginationModel,
    sortModel,
    encounterId,
    setCurrentEncounterId,
    flowsheetGroups,
    filter,
  ]);

  const sharedDataGridProps = {
    sx: {
      "& .MuiDataGrid-virtualScroller": {
        maxHeight: "300px",
      },
    },
    pageSizeOptions: [5, 10, 20],
    paginationMode: "server",
    paginationModel: paginationModel,
    onPaginationModelChange: (model) => {
      setPaginationModel(model);
      setShouldFetchFlowsheet(true);
    },
    sortingMode: "server",
    sortModel: sortModel,
    onSortModelChange: (sortModel) => {
      setSortModel(sortModel);
      setShouldFetchFlowsheet(true);
    },
    filterMode: "server",
    onFilterModelChange: (filterModel) => {
      setFilter(filterModel);
      setShouldFetchFlowsheet(true);
    },
    loading: shouldFetchFlowsheet,
    columns: flowsheetTableFields,
    rows: rows || [],
    rowCount: rowCount || 0,
    // Current server side pagination does not support most column options
    disableColumnMenu: true,
    highlightField: "createdAt",
    slotProps: {
      filterPanel: {
        logicOperators: [GridLogicOperator.And],
      },
      toolbar: {
        onViewStateReady: () => setViewStateReady(true),
      },
    },
    tableId: `flowsheet-${encounterId === null ? "patient" : "encounter"}-data-table`, // this shared the same table ID for saved views across index and encounter tables
  } satisfies Partial<BaseTableProps<Flowsheet>>;

  function VisitsDrawerElement() {
    return (
      <Card className="visit-drawer-card">
        <CardContent className="flowsheets">
          <Typography variant="subtitle1">
            <span className="section-header-title">Flowsheet Data</span>
            <span className="section-header-subtitle">
              &nbsp;for this visit
            </span>
          </Typography>
          {isCardEmpty ? (
            <EmptyCard type="Flowsheet Data" />
          ) : (
            <BaseTable<Flowsheet>
              tableHeight={"auto"}
              {...sharedDataGridProps}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  function PatientCardElement() {
    return (
      <>
        {open && selectedEncounterId && (
          <VisitsDrawer
            toggleDrawer={() => toggleOpen(null)}
            initialVisitId={selectedEncounterId}
          />
        )}
        <AccordionTable<Flowsheet>
          title={"Flowsheet Data"}
          highlightCount={highlightTotal}
          totalCount={countTotal ?? 0}
          tableHeight={400}
          triggerFetch={() => setShouldFetchFlowsheet(true)}
          {...sharedDataGridProps}
        />
      </>
    );
  }

  if (!flowsheetGroups || flowsheetGroups.length === 0) {
    return;
  }

  if (encounterId === "index" || encounterId === null) {
    return PatientCardElement();
  } else {
    return VisitsDrawerElement();
  }
}
